<script setup lang="ts">
import { useToast, type Toast } from '../composables/useToast'

/**
 * PToast / PToastContainer — container global pour les toasts éphémères.
 *
 * À monter une seule fois côté `App.vue`. Affiche tous les toasts de la
 * file `useToast()` en pile bas-droite. Les call-sites (composables use
 * case) utilisent `useToast().success("...")`, ils n'instancient PAS ce
 * composant.
 *
 * Style Revelio : flat, bordure 1px coloured, dot 6×6 à gauche au lieu
 * d'icône bloc. Animation slide-from-right + fade.
 */
const { toasts, dismiss } = useToast()

function levelLabel(level: Toast['level']): string {
    switch (level) {
        case 'success': return 'OK'
        case 'error':   return 'ERR'
        case 'warning': return 'WARN'
        case 'info':    return 'INFO'
    }
}
</script>

<template>
    <Teleport to="body">
        <div class="p-toast-container" role="region" aria-live="polite">
            <TransitionGroup name="p-toast">
                <div
                    v-for="toast in toasts"
                    :key="toast.id"
                    :class="['p-toast', `p-toast--${toast.level}`]"
                    role="alert"
                >
                    <span class="p-toast__dot" aria-hidden="true"></span>
                    <div class="p-toast__content">
                        <span class="p-toast__level">{{ levelLabel(toast.level) }}</span>
                        <span class="p-toast__message">{{ toast.message }}</span>
                    </div>
                    <button
                        type="button"
                        class="p-toast__close"
                        :aria-label="$t('common.closeNotification')"
                        @click="dismiss(toast.id)"
                    >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
                            <path d="M3 3l10 10M13 3L3 13" />
                        </svg>
                    </button>
                </div>
            </TransitionGroup>
        </div>
    </Teleport>
</template>

<style scoped>
.p-toast-container {
    position: fixed;
    bottom: var(--p-space-4);
    right: var(--p-space-4);
    display: flex;
    flex-direction: column;
    gap: var(--p-space-2);
    z-index: 1100;
    pointer-events: none;
    max-width: 420px;
}

.p-toast {
    display: flex;
    align-items: flex-start;
    gap: var(--p-space-3);
    padding: 12px 14px;
    background: var(--p-color-surface);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    box-shadow: var(--p-shadow-popover);
    font-size: var(--p-text-sm);
    color: var(--p-color-ink);
    pointer-events: auto;
    min-width: 300px;
}

.p-toast__dot {
    width: 6px;
    height: 6px;
    border-radius: var(--p-radius-full);
    margin-top: 8px;
    flex-shrink: 0;
}

.p-toast--success { border-color: var(--p-color-euca-soft); }
.p-toast--success .p-toast__dot   { background: var(--p-color-euca); }
.p-toast--success .p-toast__level { color: var(--p-color-euca); }

.p-toast--error { border-color: var(--p-color-brick-soft); }
.p-toast--error .p-toast__dot   { background: var(--p-color-brick); }
.p-toast--error .p-toast__level { color: var(--p-color-brick); }

.p-toast--warning { border-color: var(--p-color-amber-soft); }
.p-toast--warning .p-toast__dot   { background: var(--p-color-amber); }
.p-toast--warning .p-toast__level { color: var(--p-color-amber); }

.p-toast--info { border-color: var(--p-color-line); }
.p-toast--info .p-toast__dot   { background: var(--p-color-ink-4); }
.p-toast--info .p-toast__level { color: var(--p-color-ink-4); }

.p-toast__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}
.p-toast__level {
    font-family: var(--p-font-mono);
    font-size: 9px;
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-widest);
}
.p-toast__message {
    color: var(--p-color-ink);
    line-height: var(--p-line-snug);
}

.p-toast__close {
    background: transparent;
    border: none;
    color: var(--p-color-ink-5);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--p-radius-sm);
    flex-shrink: 0;
    transition: color var(--p-motion-fast) var(--p-ease-out);
}
.p-toast__close:hover {
    color: var(--p-color-ink);
}

.p-toast-enter-active,
.p-toast-leave-active {
    transition:
        transform var(--p-motion-base) var(--p-ease-out),
        opacity var(--p-motion-base) var(--p-ease-out);
}
.p-toast-enter-from,
.p-toast-leave-to {
    opacity: 0;
    transform: translateX(20px);
}
</style>
